import { FC } from 'react';

import { BugoData } from '@shared/api/bugo/bugo.interface';
import { splitDeceasedName } from '@shared/api/fevent/deceasedName.util';
import { resolveObituaryTheme } from '@shared/api/fevent/obituaryTheme';
import { DeathCharacter } from '@shared/miscValues';
import { formatFeventDateString } from '@shared/utils/formatDate';

import { obituaryHeaderBackground } from './obituaryHeaderPalette';
import { ObituaryPortrait } from './ObituaryPortrait';
import { ObituaryHeaderInfo, ObituaryThemedHeader } from './ObituaryThemedHeader';

interface Props {
  bugo: BugoData;
}

//* 고인 표기 "故 ○○○ 상세 (남/82세)"
//* - 故는 訃告와 같은 바탕체(서브셋 3글자 중 하나)로 그린다 — 본문 글꼴엔 한자가 없어 대체 글꼴로 빠지면 어색함
//* - 이름 상세·성별/나이는 괄호(build-prod-preed 836b303a 인라인 JSX 레이아웃을 develop으로 수렴,
//*   flex 정렬은 공백 문자를 삼켜 inline+align-middle로 대체)
//* - 글자 비율(2026-11-02 사용자): 이름 21px 굵게 > 상세·성별/나이 17px(같은 크기·같은 색).
//*   예전엔 성별/나이가 이름과 같은 19px, 상세만 17px 회색이라 "밝음→어둠→밝음"으로 눈에 걸렸음.
//*   테마 헤더(ObituaryThemedHeader)도 같은 비율을 쓴다 — 한쪽만 바꾸지 말 것.
const DeceasedNameHeading: FC<{ deceasedInfo: ObituaryHeaderInfo }> =
  function DeceasedNameHeading({ deceasedInfo }) {
    const { sex, age } = deceasedInfo;
    //* name은 "홍길동 어거스틴" 합성값일 수 있어 분리(세례명·법명은 회색 상세 자리에)
    const { baseName: name, extraName: nameDetail } = splitDeceasedName(deceasedInfo);
    //* 띄어쓰기는 마진이 아니라 실제 공백 문자로 — 복사·스크린리더에서 "故이름상세"로 붙지 않게
    //* 故 1.07em: 바탕체 한자가 Spoqa 한글보다 7% 작아(0.86em vs 0.92em) 같은 크기면 위가 비어 낮아 보임 — 키우면 위·아래·중심 일치
    return (
      <>
        <span className="batang text-[1.07em]">{DeathCharacter}</span> {name}
        {nameDetail && (
          <>
            {' '}
            <span className="align-middle text-17 font-medium text-white/80">
              {nameDetail}
            </span>
          </>
        )}
        {(sex || age) && (
          <>
            {' '}
            <span className="whitespace-nowrap align-middle text-17 font-normal text-white/80">
              ({sex ? sex : ''}
              {sex && age ? '/' : ''}
              {age ? `${age}세` : ''})
            </span>
          </>
        )}
      </>
    );
  };

export const ObituaryHeader: FC<Props> = function ObituaryHeader({ bugo }) {
  return <ObituaryHeaderContent deceasedInfo={bugo.fevent.deceasedInfo} />;
};

interface ContentProps {
  deceasedInfo: ObituaryHeaderInfo;
  /** 저장 전 맞춘 사진의 로컬 URL. 공개 부고장은 기존 버킷 이미지를 사용한다. */
  portraitSrc?: string;
}

export const ObituaryHeaderContent: FC<ContentProps> = function ObituaryHeaderContent({
  deceasedInfo,
  portraitSrc,
}) {
  //* 고인 종교(불교·기독교·천주교)·국가유공자·무교면 디자이너 테마 헤더, 미설정은 아래 기본 헤더
  const theme = resolveObituaryTheme(deceasedInfo);
  if (theme) {
    return (
      <ObituaryThemedHeader
        deceasedInfo={deceasedInfo}
        theme={theme}
        portraitSrc={portraitSrc}
      />
    );
  }
  //* 사진형: 영정·액자는 테마 헤더와 공용(ObituaryPortrait), 글자는 왼쪽 정렬 + 세로 중앙(2026-11-02 사용자).
  //* 높이를 고정하지 말 것 — h-32 고정일 때 이름이 2줄로 꺾이면 별세일이 헤더 밖 흰 배경으로 밀려 안 보였음.
  if (portraitSrc || deceasedInfo.dpImage) {
    return (
      <div
        className="flex items-center gap-5 py-4 px-6"
        style={obituaryHeaderBackground}
        data-layout="photo"
      >
        <ObituaryPortrait portraitSrc={portraitSrc} dpImage={deceasedInfo.dpImage} />
        <div className="min-w-0 flex-1 text-left text-white">
          <p className="batang mb-2 text-30 font-bold">訃告</p>
          <p className="break-keep text-[21px] font-bold leading-6" data-line="name">
            <DeceasedNameHeading deceasedInfo={deceasedInfo} />
          </p>
          {deceasedInfo.death?.date && (
            <p className="mt-1.5 text-14 leading-5">
              {formatFeventDateString(deceasedInfo.death, false)}&nbsp;
              {deceasedInfo.bugoTerm ? deceasedInfo.bugoTerm : '별세'}
            </p>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div className="h-32 space-y-3 pt-4" style={obituaryHeaderBackground}>
        <p className="batang text-center text-30 font-bold text-white">訃告</p>
        <div className="space-y-2 text-center text-white">
          <p className="break-keep text-[21px] font-bold leading-6" data-line="name">
            <DeceasedNameHeading deceasedInfo={deceasedInfo} />
          </p>
          {deceasedInfo.death?.date && (
            <p className="text-14 leading-5">
              {formatFeventDateString(deceasedInfo.death, false)}&nbsp;
              {deceasedInfo.bugoTerm ? deceasedInfo.bugoTerm : '별세'}
            </p>
          )}
        </div>
      </div>
    );
  }
};
